/**
 * The four strategy mode cards. The glyph is drawn, not written: colored
 * gateway chips (the same chips as the rotation line and pool rows), a
 * warning mark for the fail, a mini share bar for % split.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { AlertTriangle } from 'lucide-react';

import { HintDot } from '@/components/ui';
import { cn } from '@/lib/utils';

import { STRATEGY_CARDS, gwColor } from './strategy-copy';

const Chip: FC<{ letter: string }> = ({ letter }) => (
  <span
    className="flex h-4 w-4 items-center justify-center rounded font-mono text-[10px] font-bold text-white"
    style={{ backgroundColor: gwColor(letter.charCodeAt(0) - 65) }}
  >
    {letter}
  </span>
);

const Glyph: FC<{ spec: string }> = ({ spec }) => {
  if (spec === 'bar') {
    return (
      <span className="flex h-4 w-24 overflow-hidden rounded">
        {[50, 30, 20].map((share, i) => (
          <span
            key={i}
            className="flex items-center justify-center text-[9px] font-bold text-white"
            style={{ width: `${share}%`, backgroundColor: gwColor(i) }}
          >
            {share}
          </span>
        ))}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1">
      {[...spec].map((ch, i) =>
        ch === '→' ? (
          <span key={i} className="text-[10px] text-muted-foreground">
            →
          </span>
        ) : ch === '!' ? (
          <AlertTriangle key={i} className="h-3.5 w-3.5 text-warning" />
        ) : (
          <Chip key={i} letter={ch} />
        ),
      )}
    </span>
  );
};

interface Props {
  strategy: string;
  onSelect: (strategy: string) => void;
}

/** The radio mark of ChoiceChips, on a light card: empty ring when free, filled brand colour with a white dot when picked. */
export const RadioMark: FC<{ on: boolean }> = ({ on }) => (
  <span
    aria-hidden
    className={cn(
      'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-[1.5px] transition-colors duration-200',
      on ? 'border-primary bg-primary' : 'border-muted-foreground/50',
    )}
  >
    <span className={cn('h-1.5 w-1.5 rounded-full bg-primary-foreground transition-transform duration-200 motion-reduce:transition-none', on ? 'scale-100' : 'scale-0')} />
  </span>
);

export const StrategyCards: FC<Props> = ({ strategy, onSelect }) => (
  <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Gateway strategy">
    {STRATEGY_CARDS.map((card) => {
      const on = strategy === card.value;
      return (
        <button
          key={card.value}
          type="button"
          role="radio"
          aria-checked={on}
          onClick={() => onSelect(card.value)}
          className={cn(
            'flex flex-col items-start gap-2 rounded-lg border px-3.5 py-3 text-left transition-colors duration-200',
            on ? 'border-primary bg-primary/5 shadow-sm' : 'border-input bg-background hover:border-primary/50',
          )}
        >
          <span className="flex w-full items-center gap-2">
            <RadioMark on={on} />
            <span className="text-[13px] font-semibold">{card.title}</span>
            <span className="ml-auto">
              <HintDot label={card.hint} />
            </span>
          </span>
          <span className="pl-6">
            <Glyph spec={card.glyph} />
          </span>
          <span className="pl-6 text-xs leading-snug text-muted-foreground">{card.caption}</span>
        </button>
      );
    })}
  </div>
);
